/**
 * networkReady — "is there actually a network right now, and if not, tell me
 * the moment there is" for retry loops that must not fail closed on a blip.
 *
 * WHY THIS EXISTS (2026-07-30)
 * ---------------------------
 * The native OAuth return is the worst network moment in the app's life, and
 * the fail-closed swap guard in finalizeOAuth was sitting right in it.
 *
 * Live evidence (PostHog, 2026-07-29, distinct_id 019faefb-…, Android):
 *   22:45:16.785  $pageview  https://localhost/          ← WebView booted FRESH
 *   22:45:23.606  realtime reconnect looping (network), socketState "no-conn"
 *   22:45:27.604  oauth-swap lookup-unavailable (invoke-error)
 *   22:45:34.015  user retries from /login?oauth_error=swap_unavailable
 *   22:45:41.327  oauth-swap lookup-unavailable (invoke-error)  ← AGAIN
 *
 * Three things that timeline proves, none of which were assumed:
 *  1. The app COLD-BOOTS across the Chrome Custom Tab round-trip — the pageview
 *     is on `/`, not a resumed SPA route. So the finalizer fires its first
 *     network call seconds after a fresh WebView start.
 *  2. The socket had NO connection object at all (`no-conn`) and the Capacitor
 *     Network plugin had just reported a connectivity TRANSITION (trigger
 *     "network") 3+ times inside 60s. The device genuinely had no usable route.
 *  3. The outage spanned ~25s across TWO independent OAuth round-trips. The old
 *     budget — 3 attempts, 400ms/900ms backoff, ~1.3s total — was an order of
 *     magnitude too small and was spent entirely into a dead radio.
 *
 * WHAT IS *NOT* HAPPENING: the realtime reconnect loop is not breaking the
 * lookup. `supabase.functions.invoke` is a plain HTTPS fetch; cycling a
 * websocket cannot fail it. The reconnect loop and the invoke failure are
 * CO-SYMPTOMS of the same missing network, not cause and effect. Fixing the
 * realtime layer would not have fixed this.
 *
 * So the fix is a real connectivity signal rather than a bigger magic number:
 * a caller waits only as long as the network is actually down, and returns the
 * instant it is back. A healthy device pays nothing — `isOnlineNow()` resolves
 * true on the first check and every wait is skipped.
 *
 * Native uses the Capacitor Network plugin (already installed, see §18); web
 * falls back to `navigator.onLine`. Everything is fail-OPEN: if connectivity
 * cannot be determined we report "online" so a caller can still try, because a
 * failed probe must never be the reason a request is not attempted.
 */
import { Network } from "@capacitor/network";
import { isNativeApp } from "@/lib/nativeApp";

/** Poll interval backing up the event listener. The Capacitor Network plugin
 *  is known to flap on these devices (§13, "realtime reconnect looping"), so a
 *  missed or duplicated event must never leave a caller hanging for its whole
 *  budget — the poll is the floor under the listener. */
const POLL_INTERVAL_MS = 500;

/** Current connectivity, best effort. Never throws; unknown → `true`. */
export async function isOnlineNow(): Promise<boolean> {
  if (isNativeApp()) {
    try {
      const status = await Network.getStatus();
      return status.connected !== false;
    } catch {
      return true; // plugin unavailable — do not block the caller
    }
  }
  if (typeof navigator === "undefined") return true;
  return navigator.onLine !== false;
}

/**
 * Resolve as soon as the device reports a usable network, or when `timeoutMs`
 * elapses — whichever comes first.
 *
 * Returns `true` if the network was (or became) available, `false` if the wait
 * timed out while still offline. `timeoutMs <= 0` degrades to a single
 * immediate check, so callers can use it as a plain "am I online" gate.
 *
 * A cold WebView boot is the reason this polls as well as listens: the resume /
 * connectivity events that fire during the OAuth round-trip can land BEFORE
 * this module is even evaluated, so an event-only gate would wait for a signal
 * that has already passed. Whichever of the two fires first wins.
 */
export async function waitForOnline(timeoutMs: number): Promise<boolean> {
  if (await isOnlineNow()) return true;
  if (timeoutMs <= 0) return false;

  return new Promise<boolean>((resolve) => {
    let settled = false;
    let poll: ReturnType<typeof setInterval> | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let removeListener: (() => void) | null = null;

    const finish = (online: boolean) => {
      if (settled) return;
      settled = true;
      if (poll) clearInterval(poll);
      if (timer) clearTimeout(timer);
      try { removeListener?.(); } catch { /* no-op */ }
      resolve(online);
    };

    timer = setTimeout(() => finish(false), timeoutMs);

    poll = setInterval(() => {
      void isOnlineNow().then((online) => { if (online) finish(true); });
    }, POLL_INTERVAL_MS);

    if (isNativeApp()) {
      // addListener resolves asynchronously; if we already settled by then,
      // remove the handle immediately so it cannot leak past this call.
      void Network.addListener("networkStatusChange", ({ connected }) => {
        if (connected) finish(true);
      })
        .then((handle) => {
          removeListener = () => { void handle.remove(); };
          if (settled) removeListener();
        })
        .catch(() => { /* poll still covers us */ });
    } else if (typeof window !== "undefined") {
      const onOnline = () => finish(true);
      window.addEventListener("online", onOnline);
      removeListener = () => window.removeEventListener("online", onOnline);
      if (settled) removeListener();
    }
  });
}
